/**
 * @module geo/components
 *
 */


import {ExtensionType} from '../types/extensions';
import {PanelWidgetExtension, PanelWidgetExtensionProperties, WidgetPanelType} from '../types/extensions/panel.widget';
import {SearchResultExtension, SearchResultExtensionProperties} from '../types/extensions/search.result';
import {TooltipExtension, TooltipExtensionProperties} from '../types/extensions/tooltip';

import {Component} from './component';
import { SearchContainer } from '../types/search.container';
import { Feature } from '../types';
import { InfosheetExtensionProperties, InfosheetExtension } from '../types/extensions/infosheet';

/**
 * Left side panel in GP layout
 * Define visibility of all the side panel
 * 
 * To define visibility of sub panel see each sub components : [[InfoSheetPanel]],
 * [[ExplorePanel]],[[SearchResultPanel]],[[AnalysesResultsPanel]],[[StatsResultsPanel]]
 * @since 2.0.1
 */
export class SidePanel extends Component {
  defaultExtensionType = ExtensionType.WIDGET_PANEL;
}
/**
 * InfosheetPanel is part of [[SidePanel]]
 * @since 2.0.1
 */
export class InfoSheetPanel extends Component {
  defaultExtensionType = ExtensionType.INFOSHEET;

  public buildExtension(
    properties: InfosheetExtensionProperties
  ): InfosheetExtension {
    const type = this.defaultExtensionType;
    const props = { ...properties };
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (f, infosheet, search, isc) => {
        return (
          (!opt.infosheetId || opt.infosheetId === infosheet.id) &&
          (!opt.searchId || opt.searchId === search.id) &&
          (!opt.infosheetName || opt.infosheetName === infosheet.name) &&
          (!opt.searchName || opt.searchName === search.name)
        );
      };
    }
    return { type, properties: props } as InfosheetExtension;
  }
}


/**
 * Explore panel is part of [[SidePanel]]
 * @since v2.0.1
 */
export class ExplorePanel extends Component {
  defaultExtensionType = ExtensionType.WIDGET_PANEL;
  public buildExtension(properties: PanelWidgetExtensionProperties):
      PanelWidgetExtension {
    const type = this.defaultExtensionType;
    properties.type = WidgetPanelType.WIDGETSIDE;
    return {type, properties} as PanelWidgetExtension;
  }
}

/**
 * The search result panel ( is part of [[SidePanel]]).
 * @since v2.0.1
 */
export class SearchResultPanel extends Component {
  
  public buildExtension(properties: SearchResultExtensionProperties|
                        TooltipExtensionProperties): SearchResultExtension | TooltipExtension {
    if (properties.extensionType) {
      if (properties.extensionType === 'result') {
        return this.buildResultExtension(
            properties as SearchResultExtensionProperties);
      }
      if (properties.extensionType === 'tooltip') {
        return this.buildTooltipExtension(
            properties as TooltipExtensionProperties);
      }

      throw Error('unknown SearchResultPanel extensionType. available values are result and tooltip');
    }

    return this.buildTooltipExtension(properties as TooltipExtensionProperties);
  }


  /**
   * build an extension object for search tooltip extension
   */
  public buildTooltipExtension(properties: TooltipExtensionProperties):
      TooltipExtension {
    const type = ExtensionType.TOOLTIP;
    const props = {...properties};
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (feature, searchContainer) => {
        return (!opt.searchId ||
                opt.searchId === searchContainer.searches[0].id) &&
            (!opt.searchName ||
             opt.searchName === searchContainer.searches[0].name);
      }
    }
    return {type, properties: props} as TooltipExtension;
  }
  /**
   * build an extension object for search result extension
   */
  public buildResultExtension(properties: SearchResultExtensionProperties):
      SearchResultExtension {
    const type = ExtensionType.SEARCHRESULT;
    const props = {...properties};
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (feature:Feature, searchContainer:SearchContainer) => {
        return (!opt.searchId ||
                opt.searchId === searchContainer.searches[0].id) &&
            (!opt.searchName ||
             opt.searchName === searchContainer.searches[0].name);
      }
    }
    return {type, properties: props} as SearchResultExtension;
  }
}

/**
 * The Analyses result panel ( is part of [[SidePanel]]).
 * @since v2.0.1
 */
export class AnalysesResultsPanel extends Component {
  defaultExtensionType = ExtensionType.ANALYSES_TOOLBAR;
}
/**
 * The stats result panel ( is part of [[SidePanel]]).
 * @since v2.0.1
 */
export class StatsResultsPanel extends Component {
  defaultExtensionType = ExtensionType.STATS_TOOLBAR;
}
